import { useCallback, useEffect, useRef } from "react"

import { EditableTitleSaveError, toast } from "@coval/ui"

interface DetachedSaveFailureToast {
  /** Toast title, e.g. "Couldn't rename the agent". */
  title: string
  /** Toast description when the error carries no user-facing message. */
  fallback: string
}

/**
 * Returns a wrapper for an `EditableTitle` save. While the calling component is mounted the field shows a rejection inline; once it has unmounted — the user pressed Escape, opened the row, or the row left the list mid-save — a rejection is raised as a toast instead, so a failed save is never silent. The wrapper rejects either way.
 */
export function useDetachedSaveFailureToast({
  title,
  fallback,
}: DetachedSaveFailureToast) {
  const isMountedRef = useRef(false)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  return useCallback(
    async (save: Promise<void>) => {
      try {
        await save
      } catch (error) {
        if (!isMountedRef.current) {
          toast.error({
            title,
            error,
            fallback,
            description:
              error instanceof EditableTitleSaveError
                ? error.message
                : undefined,
          })
        }
        throw error
      }
    },
    [fallback, title],
  )
}
